import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { Layout } from "react-grid-layout";
import { useParams } from "react-router-dom";
import { DashboardService } from "../../../service/DashboardService";
import {
  ActionTypes,
  dashboardReducer,
  DashboardState,
  IActionType,
} from "../Reducer";

export interface Widget {
  id?: string;
  type: "chatbot" | "form" | "flow" | "header" | "tutorials" | "flows";
  title: string;
  chatbotData?: any;
  formData?: any;
  flowData?: any;
  layout: Layout;
  spaceName?: string;
}

interface DashboardContextType {
  state: DashboardState;
  dispatch: React.Dispatch<IActionType>;
  loadingDashboard: boolean;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addWidget: ({
    type,
    widgetData,
  }: {
    type: "chatbot" | "form" | "flow" | "header" | "tutorials" | "flows";
    widgetData?: any;
  }) => Promise<void>;
  removeWidget: (widgetId: string) => Promise<void>;
  dashboardId: string | null;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

interface DashboardProviderProps {
  children: ReactNode;
  dashboardId?: string;
}

const initialState: DashboardState = {
  dashboard: {
    id: "",
    status: "draft",
    widgets: [],
    createdAt: "",
    updatedAt: "",
  },
  isInitialized: false,
};

export function DashboardProvider({
  children,
  dashboardId: propDashboardId,
}: DashboardProviderProps) {
  const { dashboardId: paramDashboardId } = useParams();
  const dashboardId = propDashboardId || paramDashboardId || null;

  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const getDashboard = async () => {
      if (!dashboardId) {
        setLoadingDashboard(false);
        return;
      }

      setLoadingDashboard(true);
      try {
        const response = await DashboardService.getDashboard(dashboardId);

        dispatch({
          type: ActionTypes.UPDATE_DASHBOARD,
          payload: { dashboard: response.data },
        });
      } catch (error) {
        console.error("Error fetching dashboard:", error);
      } finally {
        setLoadingDashboard(false);
      }
    };

    getDashboard();
  }, [dashboardId]);

  async function addWidget({
    type,
    widgetData,
  }: {
    type: "chatbot" | "form" | "flow" | "header" | "tutorials" | "flows";
    widgetData?: any;
  }) {
    if (!dashboardId) return;

    try {
      const id = crypto.randomUUID();

      const newWidget: any = {
        id,
        dashboardId,
        layout: {
          i: id,
          x: 0,
          y: Math.max(
            ...state.dashboard.widgets.map(
              (item) => item.layout.y + item.layout.h,
            ),
            0,
          ),
          w: 3,
          h: 3,
          minW: 2,
          minH: 3,
        },
      };

      switch (type) {
        case "chatbot":
          newWidget.type = "chatbot";
          newWidget.title = widgetData?.name ?? "Chatbot Widget";
          newWidget.chatbotData = widgetData;
          break;
        case "form":
          newWidget.type = "form";
          newWidget.title = widgetData?.name ?? "Interface Widget";
          if (widgetData?.id && widgetData?.isForm) {
            newWidget.flowData = widgetData;
          } else {
            newWidget.formData = widgetData;
          }
          break;
        case "flow":
          newWidget.type = "flow";
          newWidget.title = widgetData?.name ?? "Agent Widget";
          newWidget.flowData = widgetData;
          break;
        case "header":
          newWidget.type = "header";
          newWidget.title = "Dashboard Header";
          newWidget.layout = {
            i: id,
            x: 0,
            y: 0,
            w: 12,
            h: 3,
            minW: 12,
            minH: 3,
            maxW: 12,
          };
          break;
        case "tutorials":
          newWidget.type = "tutorials";
          newWidget.title = "Flux Tutorials";
          const tutorialsY = Math.max(
            ...state.dashboard.widgets
              .filter((w) => w.type === "header")
              .map((item) => item.layout.y + item.layout.h),
            0,
          );
          newWidget.layout = {
            i: id,
            x: 0,
            y: tutorialsY,
            w: 12,
            h: 5,
            minW: 6,
            minH: 4,
          };
          break;
        case "flows":
          newWidget.type = "flows";
          newWidget.title = "Recently Opened & Shared Files";
          const flowsY = Math.max(
            ...state.dashboard.widgets.map(
              (item) => item.layout.y + item.layout.h,
            ),
            0,
          );
          newWidget.layout = {
            i: id,
            x: 0,
            y: flowsY,
            w: 12,
            h: 6,
            minW: 6,
            minH: 4,
          };
          break;
      }

      await DashboardService.addWidget({
        dashboardId,
        widgetData: newWidget,
      });

      dispatch({
        type: ActionTypes.ADD_WIDGET,
        payload: newWidget,
      });
    } catch (error) {
      console.error("Error adding widget:", error);
    }
  }

  async function removeWidget(widgetId: string) {
    try {
      await DashboardService.removeWidget(widgetId);

      dispatch({
        type: ActionTypes.REMOVE_WIDGET,
        payload: widgetId,
      });
    } catch (error) {
      console.error("Error removing widget:", error);
    }
  }

  return (
    <DashboardContext.Provider
      value={{
        state,
        dispatch,
        loadingDashboard,
        modalOpen,
        setModalOpen,
        addWidget,
        removeWidget,
        dashboardId,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);

  if (context === undefined) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
}
